import { ColDef } from 'ag-grid-community';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { GridCellRendererEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { IDataRequestSummary, IRequestedStudy } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { DataRequestDateRendererComponent } from './data-request-date-renderer.component';
import { DataRequestStatusRenderer } from './data-request-status-renderer.component';
import { DataRequestTitleRendererComponent } from './data-request-title-renderer.component';
import { MyDataRequestTitleRendererComponent } from './my-data-request-title-renderer.component';

export type DataRequestsGridDisplayType = 'default' | 'myrequests';

interface IDataRequestExportColumn {
    key: string;
    headerName: string;
    getValue: (data: IDataRequestSummary, isAmr: boolean) => string;
}

const getExportStatus = (data: IDataRequestSummary) => {
    if (data.status === DataRequestStatusEnum.RejectedByDataProvider) {
        return 'NotApprovedByDataProvider';
    } else if (data.status === DataRequestStatusEnum.RejectedByVivli) {
        return 'NotApprovedByVivli';
    } else if (data.status === DataRequestStatusEnum.RejectedByIRP) {
        return 'NotApprovedByIRP';
    } else return data.status;
};

// The CSV export columns, in file order. Used by the grid's hidden export columns and by the paged
// grid's server-side export, so both write the same file.
const dataRequestExportColumns: IDataRequestExportColumn[] = [
    { key: 'requestId', headerName: 'Request ID', getValue: (data) => data.idString.replace('Vivli ID: ', '') },
    { key: 'requestTitle', headerName: 'Data Request', getValue: (data) => data.requestTitle },
    { key: 'userName', headerName: 'Requester', getValue: (data) => data.userName || 'Unknown' },
    { key: 'userEmail', headerName: "Requester's Email", getValue: (data) => data.userEmail || 'Unknown' },
    {
        key: 'pi',
        headerName: 'Principal Investigator',
        getValue: (data, isAmr) => (isAmr ? data.amrResearcherDetails?.researcherName : DataRequestFormatService.getLeadResearcher(data))
    },
    {
        key: 'piEmail',
        headerName: 'PI Email',
        getValue: (data, isAmr) => (isAmr ? data.amrResearcherDetails?.researcherEmail : data.leadResearcherTeamMemberDetail?.email)
    },
    { key: 'status', headerName: 'Request Status', getValue: getExportStatus },
    {
        key: 'dateOfLastAction',
        headerName: 'Date of Last Update',
        getValue: (data) => moment(data.updatedDate).format('MM/DD/YY hh:mm a')
    },
    {
        key: 'dataContributorOrganizations',
        headerName: 'Data Contributor Organizations',
        getValue: (data) => Array.from(new Set(data.requestedStudies.map((study) => study.organizationName))).join(';')
    }
];

export const dataRequestExportColumnKeys = dataRequestExportColumns.map((c) => c.key);
export const dataRequestExportHeaders = dataRequestExportColumns.map((c) => c.headerName);

// One CSV row, keyed in export column order, for exports built outside ag-grid.
export const mapDataRequestSummaryToExportRow = (data: IDataRequestSummary, isAmr: boolean): Record<string, string> =>
    Object.fromEntries(dataRequestExportColumns.map((c) => [c.key, c.getValue(data, isAmr)]));

export const dataRequestGridCustomComponents = [
    {
        component: DataRequestDateRendererComponent,
        name: GridCellRendererEnum.DataRequestDate
    },
    {
        component: DataRequestStatusRenderer,
        name: GridCellRendererEnum.DataRequestStatus
    },
    {
        component: DataRequestTitleRendererComponent,
        name: GridCellRendererEnum.DataRequestTitle
    },
    {
        component: MyDataRequestTitleRendererComponent,
        name: GridCellRendererEnum.MyDataRequestTitle
    }
];

const idComparator = (a, b, nodeA, nodeB) => {
    const aVal: IDataRequestSummary = nodeA.data;
    const bVal: IDataRequestSummary = nodeB.data;

    return aVal.idString.localeCompare(bVal.idString);
};

const updatedDateComparator = (a, b, nodeA, nodeB) => {
    const aVal: IDataRequestSummary = nodeA.data;
    const bVal: IDataRequestSummary = nodeB.data;

    return aVal.updatedDate < bVal.updatedDate ? -1 : 1;
};

const filterByList = (filter, filterTextValues: string, filterText: string) => {
    const filterByList = filterTextValues?.split(':::') || [];
    const filterTextValuesLowerCase = filterByList.filter((v) => v).map((t) => t.toLowerCase());
    const filterTextLowerCase = filterText.toString().toLowerCase();
    switch (filter) {
        case 'contains':
            return filterTextValuesLowerCase.some((v) => v.indexOf(filterTextLowerCase) >= 0);
        case 'notContains':
            return !filterTextValuesLowerCase.some((v) => v.indexOf(filterTextLowerCase) === -1);
        case 'equals':
            return filterTextValuesLowerCase.some((v) => v === filterTextLowerCase);
        case 'notEqual':
            return filterTextValuesLowerCase.some((v) => v != filterTextLowerCase);
        case 'startsWith':
            return filterTextValuesLowerCase.some((v) => v.indexOf(filterTextLowerCase) === 0);
        case 'endsWith':
            return filterTextValuesLowerCase.some((v) => {
                const index = v.lastIndexOf(filterTextLowerCase);
                return index >= 0 && index === v.length - filterTextLowerCase.length;
            });
        default:
            // should never happen
            console.warn('invalid filter type ' + filter);
            return false;
    }
};

interface UseDataRequestGridColumnsProps {
    displayType?: DataRequestsGridDisplayType;
    isPresumedResearcher?: boolean;
    initialSortField?: string;
    initialSortOrder?: 'asc' | 'desc';
}

// The column definitions shared by the client-side and the paged Data Requests grids.
export const useDataRequestGridColumns = ({
    displayType,
    isPresumedResearcher,
    initialSortField,
    initialSortOrder
}: UseDataRequestGridColumnsProps): ColDef[] => {
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const { isAmr } = useAssignedAppTypeContext();

    const exportCols: ColDef[] = dataRequestExportColumns.map((c) => ({
        field: c.key,
        headerName: c.headerName,
        hide: true,
        valueGetter: (params) => c.getValue(params.data, isAmr)
    }));

    const hasCustomSort = !!initialSortField;
    const sortOrder = initialSortOrder || 'desc';

    return [
        ...(hasCustomSort
            ? [
                  {
                      field: initialSortField,
                      hide: true,
                      sortable: true,
                      initialSort: sortOrder as 'asc' | 'desc'
                  }
              ]
            : []),
        {
            field: 'customTitle',
            headerName: 'Data Request',
            filter: true,
            sortable: true,
            cellRenderer: displayType === 'myrequests' ? GridCellRendererEnum.MyDataRequestTitle : GridCellRendererEnum.DataRequestTitle,
            comparator: displayType === 'myrequests' ? updatedDateComparator : idComparator,
            ...(!hasCustomSort ? { initialSort: 'desc' as const } : {}),
            filterParams: {
                textCustomComparator: filterByList
            },
            filterValueGetter: (params) => {
                const data: IDataRequestSummary = params.data;
                const idString = data.idString.replace('Vivli ID: ', '');
                const leadResearcherDetail = data.leadResearcherTeamMemberDetail;
                const leadResearcher = leadResearcherDetail
                    ? `${leadResearcherDetail.firstName} ${leadResearcherDetail.lastName}`
                    : 'Unknown';
                const requestStudies = params.data.requestedStudies?.map((study: IRequestedStudy) => study.organizationName);

                return [
                    data.requestTitle,
                    idString,
                    data.userName,
                    leadResearcher,
                    data.userEmail,
                    data.doiStem,
                    leadResearcherDetail?.firstName,
                    leadResearcherDetail?.lastName,
                    leadResearcherDetail?.email,
                    ...requestStudies
                ]
                    .filter((s) => s)
                    .join(':::');
            }
        },
        {
            field: 'customStatus',
            headerName: 'Status',
            filter: true,
            sortable: false,
            cellRenderer: GridCellRendererEnum.DataRequestStatus,
            cellRendererParams: {
                hideStatus: false,
                alignRight: isPresumedResearcher,
                showRemoveButton: isPresumedResearcher
            },
            filterParams: {
                textCustomComparator: filterByList
            },
            filterValueGetter: (params) => {
                const data: IDataRequestSummary = params.data;
                const status = DataRequestFormatService.getApprovalStatuses(data);
                const message = DataRequestFormatService.mapStatusToMessage(data.status, isAmr, duaTranslation);
                const approved = status.approved > 0 && 'approved';
                const available = status.available > 0 && 'available';
                const pending = status.pending > 0 && 'pending';
                const notApproved = status.notApproved > 0 && 'not approved';
                return [message, approved, available, pending, notApproved].join(':::');
            }
        },
        ...exportCols
    ];
};
